import { Body, Button, Icon } from '@assembly-js/design-system';
import { Auth } from 'aws-amplify';
import clsx from 'clsx';
import { ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { LOGIN_PAGE_BASE_PATH } from 'src/constants';
import { PortalConfigContext, RouteContext } from 'src/context';
import {
  resolveStudioReferrer,
  STUDIO_REFERRER,
} from 'src/routes/onboarding/setup/constants';
import { useOnboardingQueryParams } from 'src/routes/onboarding/setup/hooks/useOnboardingQueryParams';
import { buildStudioSignupPath } from 'src/routes/onboarding/setup/utils/buildStudioSignupPath';
import { useSendAccessLinkMutation } from 'src/services/api/unauthApi';
import { UrlUtils } from 'src/utils';
import {
  getAmplifyAuthConfig,
  getAuthRequestInfo,
  GetSignInMetadata,
  GetSignInMetadataInput,
  SignInWithGoogle,
} from 'src/utils/AuthUtils';

import { StudioLoginCredentialsForm } from './StudioLoginCredentialsForm';
import { StudioMfaChallenge } from './StudioMfaChallenge';
import { StudioPageFrame } from './StudioPageFrame';
import { STUDIO_PRIMARY_BUTTON_CLASS } from './studioStyles';
import {
  completeInternalUserSignIn,
  completeMfaSignIn,
  InternalUserMfaChallenge,
  startPasswordSignIn,
  tryIdentityProviderSignIn,
} from './utils/studioInternalUserSignIn';

type StudioLoginStep =
  | 'credentials'
  | 'mfa'
  | 'signing-in'
  | 'link-sent'
  | 'link-resent'
  | 'expired';

interface StudioLoginQueryParams {
  email?: string;
  magic_link?: string;
  reason?: string;
  auth?: string;
}

export interface StudioLoginProps {
  onAuthComplete: () => void;
}

const bodyWithEmail = (template: string, email: string): ReactNode => {
  const [before, ...rest] = template.split(email);
  if (rest.length === 0) {
    return template;
  }
  return (
    <>
      {before}
      <span className="text-black">{email}</span>
      {rest.join(email)}
    </>
  );
};

/**
 * Unified Studio login: passwordless magic link by default, optional password
 * sign-in (Raycast-style CTA), plus MFA when required.
 */
export const StudioLogin = ({ onAuthComplete }: StudioLoginProps) => {
  const { t } = useTranslation();
  const { query }: { query: StudioLoginQueryParams } = useContext(RouteContext);
  const {
    prompt,
    workspaceTemplateId,
    workspaceTemplateName,
    templateId,
    next,
    referrer,
  } = useOnboardingQueryParams();
  const portalConfig = useContext(PortalConfigContext);
  const [sendAccessLink] = useSendAccessLinkMutation();

  const magicLink = query.magic_link;
  const linkAlreadyDead =
    query.reason === 'link_expired' || query.reason === 'link_used';

  const computeInitialStep = (): StudioLoginStep => {
    if (magicLink && query.email) {
      return linkAlreadyDead ? 'expired' : 'signing-in';
    }
    return 'credentials';
  };

  const [step, setStep] = useState<StudioLoginStep>(computeInitialStep);
  const [email, setEmail] = useState(query.email ?? '');
  const [isBusy, setIsBusy] = useState(false);
  const [mfaChallenge, setMfaChallenge] =
    useState<InternalUserMfaChallenge | null>(null);
  const signInAttempted = useRef(false);

  const runMagicLinkSignIn = async (linkEmail: string, link: string) => {
    try {
      const requestInfo = await getAuthRequestInfo();
      Auth.configure(
        getAmplifyAuthConfig({
          baseAuthConfig: portalConfig.AWS.Auth,
          authenticationFlowType: 'CUSTOM_AUTH',
        }),
      );
      const cognitoUser = await Auth.signIn(linkEmail, undefined, {
        ...GetSignInMetadata(undefined, requestInfo),
        type: 'magicLink',
      });
      const authedUser = await Auth.sendCustomChallengeAnswer(
        cognitoUser,
        link,
        {
          ...GetSignInMetadata(undefined, requestInfo),
          type: 'magicLink',
        },
      );
      await completeInternalUserSignIn(
        authedUser,
        linkEmail,
        portalConfig.AWS.Auth.userPoolWebClientId,
        onAuthComplete,
      );
    } catch (error) {
      console.error('studio magic link login failed', error);
      setStep('expired');
    }
  };

  useEffect(() => {
    if (
      signInAttempted.current ||
      step !== 'signing-in' ||
      !magicLink ||
      !query.email
    ) {
      return;
    }
    signInAttempted.current = true;
    void runMagicLinkSignIn(query.email, magicLink);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleContinueWithGoogle = () => {
    const customStateParams: Record<string, string> = {
      referrer: resolveStudioReferrer(referrer) || STUDIO_REFERRER,
    };
    if (prompt) {
      customStateParams.prompt = prompt;
    }
    if (workspaceTemplateId) {
      customStateParams.template_id = workspaceTemplateId;
      if (workspaceTemplateName) {
        customStateParams.template_name = workspaceTemplateName;
      }
    }
    if (templateId) {
      customStateParams.templateId = templateId;
    }
    if (next) {
      customStateParams.next = next;
    }
    const customState = `${LOGIN_PAGE_BASE_PATH.replace(/^\//, '')}?${UrlUtils.FormatUrlQueryToString(
      customStateParams,
    )}`;
    void SignInWithGoogle(customState);
  };

  const sendLoginLink = async (
    targetEmail: string,
    nextStep: StudioLoginStep,
  ) => {
    setIsBusy(true);
    try {
      await sendAccessLink({
        email: targetEmail,
        accessType: 'internalLogin',
      }).unwrap();
      setEmail(targetEmail);
      setStep(nextStep);
    } finally {
      setIsBusy(false);
    }
  };

  const handleCredentialsSubmit = async ({
    email: submittedEmail,
    password,
  }: {
    email: string;
    password: string;
  }) => {
    if (password) {
      const result = await startPasswordSignIn({
        email: submittedEmail,
        password,
        pathname: window.location.pathname,
        query: query as GetSignInMetadataInput,
      });

      if (result.status === 'idp_redirect') {
        return;
      }
      if (result.status === 'error') {
        throw new Error(result.message);
      }
      if (result.status === 'mfa') {
        setMfaChallenge(result.challenge);
        setEmail(submittedEmail);
        setStep('mfa');
        return;
      }
      await completeInternalUserSignIn(
        result.user,
        submittedEmail,
        portalConfig.AWS.Auth.userPoolWebClientId,
        onAuthComplete,
      );
      return;
    }

    if (
      await tryIdentityProviderSignIn({
        email: submittedEmail,
        pathname: window.location.pathname,
      })
    ) {
      return;
    }

    await sendLoginLink(submittedEmail, 'link-sent');
  };

  const handleMfaSubmit = async (code: string) => {
    if (!mfaChallenge) {
      return;
    }
    const result = await completeMfaSignIn({
      user: mfaChallenge.user,
      code,
      query: query as GetSignInMetadataInput,
    });
    if (result.status === 'error') {
      throw new Error(result.message);
    }
    await completeInternalUserSignIn(
      result.user,
      mfaChallenge.email,
      portalConfig.AWS.Auth.userPoolWebClientId,
      onAuthComplete,
    );
  };

  const goToCredentialsStep = () => {
    setMfaChallenge(null);
    setStep('credentials');
  };

  if (step === 'credentials') {
    return (
      <StudioLoginCredentialsForm
        initialEmail={query.email ?? ''}
        initialPassword={query.auth ?? ''}
        onContinueWithGoogle={handleContinueWithGoogle}
        onSubmitCredentials={handleCredentialsSubmit}
        footerPrompt={t(
          ($) => $.page_onboarding.studio.login.switch_to_signup_prompt,
        )}
        footerActionLabel={t(
          ($) => $.page_onboarding.studio.login.switch_to_signup_action,
        )}
        footerActionHref={buildStudioSignupPath({
          prompt,
          workspaceTemplateId,
          workspaceTemplateName,
          templateId,
          next,
        })}
      />
    );
  }

  if (step === 'mfa' && mfaChallenge) {
    return (
      <StudioMfaChallenge
        email={mfaChallenge.email}
        onSubmitCode={handleMfaSubmit}
      />
    );
  }

  if (step === 'signing-in') {
    return (
      <StudioPageFrame>
        <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
        <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
          {t(($) => $.page_onboarding.studio.login.signing_in)}
        </Body>
      </StudioPageFrame>
    );
  }

  const messageScreens: Record<
    'link-sent' | 'link-resent' | 'expired',
    {
      title: string;
      body: ReactNode;
      primaryLabel: string;
      onPrimary: () => void;
      secondaryLabel: string;
      onSecondary: () => void;
    }
  > = {
    'link-sent': {
      title: t(($) => $.page_onboarding.studio.login.link_sent_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.link_sent_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.resend_email),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(($) => $.page_onboarding.studio.login.return_to_login),
      onSecondary: goToCredentialsStep,
    },
    'link-resent': {
      title: t(($) => $.page_onboarding.studio.login.resent_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.resent_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.send_again),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(
        ($) => $.page_onboarding.studio.login.use_different_email,
      ),
      onSecondary: goToCredentialsStep,
    },
    expired: {
      title: t(($) => $.page_onboarding.studio.login.expired_title),
      body: bodyWithEmail(
        t(($) => $.page_onboarding.studio.login.expired_subtitle, { email }),
        email,
      ),
      primaryLabel: t(($) => $.page_onboarding.studio.login.send_new_link),
      onPrimary: () => {
        void sendLoginLink(email, 'link-resent');
      },
      secondaryLabel: t(
        ($) => $.page_onboarding.studio.login.use_different_email,
      ),
      onSecondary: goToCredentialsStep,
    },
  };

  const screen =
    messageScreens[step as 'link-sent' | 'link-resent' | 'expired'];

  return (
    <StudioPageFrame>
      <Icon icon="AssemblyLogo" className="mb-3 h-8 w-8 text-black" />
      <Body className="font-studio text-2xl text-black text-center sm:text-[26px]">
        {screen.title}
      </Body>
      <Body
        size="lg"
        className="mb-0 mt-3 font-studio text-center text-[#808080]"
      >
        {screen.body}
      </Body>
      <div className="mt-12 flex w-full flex-col gap-[17px]">
        <Button
          type="button"
          variant="studio"
          className={clsx('h-12 w-full', STUDIO_PRIMARY_BUTTON_CLASS)}
          label={screen.primaryLabel}
          disabled={isBusy}
          loading={isBusy}
          onClick={screen.onPrimary}
        />
        <button
          type="button"
          onClick={screen.onSecondary}
          className="w-full cursor-pointer border-none bg-transparent text-center text-[14px] leading-[22px] text-black/50"
        >
          {screen.secondaryLabel}
        </button>
      </div>
    </StudioPageFrame>
  );
};
